import React from 'react';
import { CheckCircle, Circle, AlertCircle } from 'lucide-react';
import StepCard from './StepCard';
import { STEP_STATUS } from '../constants';

const WorkflowContainer = ({
  title,
  description,
  icon: Icon,
  steps,
  stepsData = {},
  onStartStep,
  onCompleteStep,
  onAssignUser,
  users = [],
  isAdmin = false,
  disabled = false,
  color = 'teal'
}) => {
  // SECTION 5 & 6: Calculate progress ONLY from valid step IDs
  // Filter stepsData to only include valid step IDs from the steps array
  const validStepIds = new Set(steps.map(s => s.id));
  
  const completedSteps = steps.filter(step => {
    const data = stepsData[step.id];
    return data && data.status === STEP_STATUS.COMPLETED;
  }).length;
  
  const totalSteps = steps.length;
  
  // SECTION 5: Calculate progress, never exceed 100%
  const progressPercent = totalSteps > 0 
    ? Math.min(Math.round((completedSteps / totalSteps) * 100), 100) 
    : 0;

  // Find current active step (first non-completed step)
  const currentStepIndex = steps.findIndex(step => {
    const data = stepsData[step.id];
    return !data || data.status !== STEP_STATUS.COMPLETED;
  });

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className={`bg-gradient-to-r from-${color}-50 to-white p-6 border-b border-gray-100`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {Icon && (
              <div className={`w-14 h-14 rounded-xl bg-${color}-100 flex items-center justify-center`}>
                <Icon size={28} className={`text-${color}-600`} />
              </div>
            )}
            <div>
              <h3 className="text-xl font-bold text-gray-800">{title}</h3>
              <p className="text-sm text-gray-500 mt-1">{description}</p>
            </div>
          </div>
          
          {/* Progress Badge */}
          <div className="text-right">
            <div className={`inline-flex items-center px-4 py-2 rounded-full ${
              progressPercent === 100 
                ? 'bg-green-100 text-green-700' 
                : progressPercent > 0 
                  ? 'bg-blue-100 text-blue-700' 
                  : 'bg-gray-100 text-gray-600'
            }`}>
              {progressPercent === 100 ? (
                <CheckCircle size={18} className="mr-2" />
              ) : progressPercent > 0 ? (
                <Circle size={18} className="mr-2" />
              ) : (
                <AlertCircle size={18} className="mr-2" />
              )}
              <span className="font-semibold">{completedSteps} / {totalSteps}</span>
              <span className="ml-1 text-sm">Steps</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">{progressPercent}% Complete</p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mt-4">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className={`h-2 rounded-full transition-all duration-500 ${
                progressPercent === 100 ? 'bg-green-500' : 'bg-teal-500'
              }`}
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      </div>

      {/* Steps List */}
      <div className="p-6 space-y-4">
        {steps.map((step, index) => {
          const stepData = stepsData[step.id];
          const prevStepData = index > 0 ? stepsData[steps[index - 1].id] : null;
          
          // A step can only be started if previous step is completed (or it's the first step)
          const canStart = index === 0 || prevStepData?.status === STEP_STATUS.COMPLETED;
          const isDisabled = disabled || (!canStart && stepData?.status !== STEP_STATUS.IN_PROGRESS && stepData?.status !== STEP_STATUS.COMPLETED);

          return (
            <StepCard
              key={step.id}
              step={step}
              stepData={stepData}
              onComplete={onCompleteStep}
              onStart={onStartStep}
              onAssignUser={onAssignUser}
              users={users}
              isAdmin={isAdmin}
              disabled={isDisabled}
            />
          );
        })}
      </div>
    </div>
  );
};

export default WorkflowContainer;
